import React from 'react';
import DOMPurify from 'dompurify';

// Strict allowlist — only basic formatting tags are allowed. Scripts,
// event handlers, links, images, iframes, etc. are stripped completely.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'br'],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true,
};

const safeHtml = (input) => {
  if (typeof input !== 'string' || input.length === 0) return '';
  return DOMPurify.sanitize(input, SANITIZE_CONFIG);
};

/**
 * Renders the live package details card shown in the registration form.
 * Used by both Home and Business sections.
 *
 * Props:
 * - planName   string  e.g. "300Mbps" or "BIZ 300Mbps"  (rendered as "unifi {planName}")
 * - priceText  string  Already-formatted price string  e.g. "RM129.00"
 * - contract   string  e.g. "27-month contract" or "30-month contract + Free 6 Month"
 * - addonLines string[]  Extra add-on rows (e.g. ["+ 65" Smart TV (+RM20)", "+ Netflix Standard (+RM44)"])
 * - recommended string  Optional recommended-use tag
 * - users      string  Optional user-capacity line
 * - includes   array   Optional list of {icon, text, subtext}
 */
export const PackageSummaryCard = ({
  planName,
  priceText,
  priceSuffix = '/month',
  priceStrikeThrough,
  contract,
  addonLines = [],
  recommended,
  users,
  includes = [],
}) => {
  return (
    <div className="mt-2 rounded-lg bg-white border border-orange-200 p-4 space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-base font-semibold text-gray-900">unifi {planName}</p>
          {addonLines.map((line, idx) => (
            <p key={`${line}-${idx}`} className="text-sm text-gray-700 mt-0.5">{line}</p>
          ))}
          <p className="text-xs text-gray-500 mt-1">{contract}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-2xl font-extrabold text-[#FF6600] leading-none">{priceText}</p>
          <p className="text-xs text-gray-500 mt-1">{priceSuffix}</p>
          {priceStrikeThrough && (
            <p className="text-xs text-gray-400 mt-0.5 line-through">{priceStrikeThrough}</p>
          )}
        </div>
      </div>

      {(recommended || users) && (
        <div className="pt-3 border-t border-orange-100 space-y-1">
          {recommended && (
            <p className="text-xs font-semibold uppercase tracking-wider text-[#FF6600]">
              Recommended: {recommended}
            </p>
          )}
          {users && <p className="text-xs text-gray-600">{users}</p>}
        </div>
      )}

      {Array.isArray(includes) && includes.length > 0 && (
        <div className="pt-3 border-t border-orange-100">
          <p className="text-[11px] font-bold tracking-[0.12em] text-gray-500 uppercase mb-2">What&apos;s Included</p>
          <ul className="space-y-1.5">
            {includes.map((item) => (
              <li key={`${item.icon}-${item.text}`} className="flex items-start gap-2 text-xs text-gray-700">
                <span className="mt-0.5 inline-block w-1.5 h-1.5 rounded-full bg-[#FF6600] shrink-0" />
                <span>
                  {/* SAFE: safeHtml() runs DOMPurify with strict allowlist (only b/strong/i/em/br) */}
                  <span className="font-semibold text-gray-900" dangerouslySetInnerHTML={{ __html: safeHtml(item.text) }} />
                  {item.subtext && (
                    /* SAFE: safeHtml() runs DOMPurify with strict allowlist (only b/strong/i/em/br) */
                    <span className="block text-gray-500" dangerouslySetInnerHTML={{ __html: safeHtml(item.subtext) }} />
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
